import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface AdminUser {
    id: string;
    username: string;
    email: string;
    role: 'admin' | 'analyst' | 'read_only';
    last_login?: string;
}

interface AdminAuthContextType {
    user: AdminUser | null;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    hasPermission: (requiredRole: 'admin' | 'analyst' | 'read_only') => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const roleHierarchy: Record<string, number> = {
    'read_only': 1,
    'analyst': 2,
    'admin': 3,
};

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AdminUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        const token = localStorage.getItem('admin_token');

        if (!token) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/admin/verify', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
            } else {
                localStorage.removeItem('admin_token');
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            localStorage.removeItem('admin_token');
        }

        setIsLoading(false);
    };

    const login = async (username: string, password: string) => {
        const response = await fetch('/api/admin/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Login failed');
        }

        const data = await response.json();
        localStorage.setItem('admin_token', data.token);
        setUser(data.user);
    };

    const logout = async () => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            try {
                await fetch('/api/admin/logout', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Logout request failed:', error);
            }
        }

        localStorage.removeItem('admin_token');
        setUser(null);
    };

    const hasPermission = (requiredRole: 'admin' | 'analyst' | 'read_only'): boolean => {
        if (!user) return false;
        return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
    };

    return (
        <AdminAuthContext.Provider value={{ user, isLoading, login, logout, hasPermission }}>
            {children}
        </AdminAuthContext.Provider>
    );
};

export const useAdminAuth = (): AdminAuthContextType => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth must be used within AdminAuthProvider');
    }
    return context;
};